import { Theme, createStyles, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { Message, useMessageQuery } from './hooks/useMessageQuery';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { RvLabel } from '../../components/Label/Label';
import { MatterTerminology } from '../../MatterTerminology';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { RvMessageCard } from './MessageCard';
import { AppUser, useCurrentUser } from '../../hooks/useCurrentUser';
import { MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import EventSystem from '../../EventSystem';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
        },
        loaderWrapper: {
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 0',
        },
        streamWrapper: {
            margin: '0 32px',
            paddingTop: '32px',
        },
    }),
);

type MessageStreamProps = {
    id: number;
    filter: string;
    message?: Message;
};

export const MessageStream: React.FC<MessageStreamProps> = (props) => {
    const classes = useStyles();

    const currentUserQuery = useCurrentUser();

    const [currentUser, setCurrentUser] = useState<AppUser | null>(null);

    // Was 'network-only' - always re-fetching on every tab mount regardless of cache, even
    // more aggressive than the app's own global default. Cached after the first load; the
    // header's Refresh button (MatterDetails.tsx) forces a re-fetch via messageQuery.refetch()
    // below.
    const messageQuery = useMessageQuery({ id: props.id }, 'cache-first');

    const [message, setMessage] = useState<Message[]>([]);

    const messageQueryRefetch = messageQuery.refetch;
    useEffect(() => {
        const onRefresh = () => {
            messageQueryRefetch();
        };
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, [messageQueryRefetch]);

    useEffect(() => {
        if (!currentUserQuery.loading && !currentUserQuery.error && currentUserQuery.data) {
            setCurrentUser(currentUserQuery.data.appUser);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },        [currentUserQuery.data, currentUserQuery.loading, currentUserQuery.error]);

    useEffect(() => {
        if (!messageQuery.loading && !messageQuery.error && messageQuery.data) {
            const tempMessage = messageQuery.data.matter.messages.message;
            setMessage(tempMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },        [messageQuery.data, messageQuery.loading, messageQuery.error]);

    useEffect(() => {
        if (props.message) {
            const tempMessage = message;

            tempMessage.unshift(props.message);

            setMessage(tempMessage);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },        [props.message]);

    const searchMessages = (data: Message[], searchString: string) => {
        return data.filter((fMessage) => Object.values(fMessage).some((value) => typeof value === 'string' && value.toLowerCase().includes(searchString.toLowerCase())));
    };

    if (messageQuery.loading || currentUserQuery.loading) {
        return (
            <div className={classes.streamWrapper}>
                <SkeletonLoader variant="timeline" rows={3} />
            </div>
        );
    }

    if (message.length === 0) {
        return (
            <div className={classes.loaderWrapper}>
                <RvLabel label={MatterTerminology.NoMessage} />
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.streamWrapper}>
                {searchMessages(message, props.filter).map((fMessage: Message, idx: number) => {
                    return (
                        <AnimatedItem key={idx} noFill={true} delay={idx * 30}>
                            <RvMessageCard
                                data={fMessage}
                                currentUser={currentUser}
                            />
                        </AnimatedItem>
                    );
                })}
            </div>
        </div>
    );
};
